import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { API_BASE_URL } from '../config';
import { getCachedBarbers, loadBarbers } from '../utils/barbersCache';
import { mapBarberFromApi } from './Reservas/Reservas';
import { Scissors, Calendar, Clock, Instagram, Award, ArrowRight, User } from 'lucide-react';
import { FaWhatsapp, FaTiktok, FaInstagram } from 'react-icons/fa6';
import barbershopImage from '../assets/modern-barbershop-interior-dark.jpg';
import ImageCarousel from './ImageCarousel';
import SocialVideos from './SocialVideos';
import InstagramPost from './InstagramPost';
import image1 from '../assets/1.jpeg';
import image2 from '../assets/2.jpeg';
import image3 from '../assets/3.jpeg';
import image4 from '../assets/4.jpeg';
import image5 from '../assets/5.jpeg';
import './LandingPage.css';

const LandingPage = () => {
    const initialBarbersCache = getCachedBarbers();
    const [barbers, setBarbers] = useState(() => initialBarbersCache || []);
    const [loadingBarbers, setLoadingBarbers] = useState(() => !initialBarbersCache);
    const [schedule, setSchedule] = useState([]);
    const [holidays, setHolidays] = useState([]);

    useEffect(() => {
        const fetchSchedule = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/barber/schedule`);
                if (res.ok) {
                    const data = await res.json();
                    setSchedule(data.weekly || (Array.isArray(data) ? data : []));
                    setHolidays(data.holidays || []);
                }
            } catch (err) {
                console.error('Error cargando horarios:', err);
            }
        };
        fetchSchedule();
    }, []);

    useEffect(() => {
        const fetchBarbers = async () => {
            try {
                const cached = getCachedBarbers();
                if (cached) {
                    setBarbers(cached);
                    setLoadingBarbers(false);
                    return;
                }

                setLoadingBarbers(true);
                const data = await loadBarbers(API_BASE_URL, mapBarberFromApi);
                setBarbers(data);
            } catch (err) {
                console.error('Error fetching barbers:', err);
                setBarbers([]);
            } finally {
                setLoadingBarbers(false);
            }
        };

        fetchBarbers();
    }, []);

    // Formatear hora "HH:MM" a "9:00 AM" / "8:00 PM"
    const formatTime12h = (timeStr) => {
        if (!timeStr) return '';
        const [h, m] = (timeStr + ':00').split(':').map(Number);
        const hour = h % 12 || 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        return `${hour}:${String(m || 0).padStart(2, '0')} ${ampm}`;
    };

    // Un bloque por día de la semana para que la vista sea específica (no agrupar Lunes–Viernes como "Cerrado")
    const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const scheduleBlocks = (() => {
        if (!schedule || schedule.length < 7) {
            return dayNames.map((name, i) => ({
                label: name,
                open: i < 6 ? '09:00' : '00:00',
                close: i < 6 ? '20:00' : '00:00',
                enabled: i < 6,
            }));
        }
        const sorted = [...schedule].sort((a, b) => a.day_of_week - b.day_of_week);
        return sorted.map((d) => ({
            label: d.day_name || dayNames[d.day_of_week] || '',
            open: d.open_time || '09:00',
            close: d.close_time || '20:00',
            enabled: !!d.enabled,
        }));
    })();

    return (
        <div className="landing-page">
            {/* Botones flotantes redes sociales */}
            <div className="floating-social">
                <a href="https://wa.me/573005158789" target="_blank" rel="noopener noreferrer" className="floating-social-btn floating-social-whatsapp" aria-label="WhatsApp">
                    <FaWhatsapp size={22} />
                </a>
                <a href="https://www.instagram.com/genuinosbarbershop_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="floating-social-btn floating-social-instagram" aria-label="Instagram">
                    <FaInstagram size={22} />
                </a>
                <a href="https://www.tiktok.com/@genuinos_barber?_r=1&_t=ZS-94cGQ3JAa26" target="_blank" rel="noopener noreferrer" className="floating-social-btn floating-social-tiktok" aria-label="TikTok">
                    <FaTiktok size={20} />
                </a>
            </div>

            {/* Navbar */}
            <nav className="navbar">
                <div className="logo">GENUINO'S</div>
                <div className="nav-links">
                    <a href="#barberos" className="nav-link">Barberos</a>
                </div>
                <div className="nav-buttons">
                    <Link to="/login" className="btn btn-outline">Iniciar Sesión</Link>
                    <Link to="/reservas" className="btn btn-primary">Reserva Ahora</Link>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero" style={{ backgroundImage: `url(${barbershopImage})` }}>
                <div className="hero-overlay"></div>
                <motion.div
                    className="hero-content"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="badge">✦ Donde el corte también es cultura</span>
                    <h1 className="hero-title">
                        Tu estilo <br />
                        empieza <span>aquí</span>
                    </h1>
                    <p className="hero-subtitle">
                        Reserva sin complicaciones. Elige tu barbero favorito y agenda tu cita en segundos.
                    </p>
                    <div className="hero-actions">
                        <Link to="/reservas" className="btn btn-primary">
                            <Calendar size={18} />
                            Reserva con tu barbero
                        </Link>
                        <a href="#barberos" className="btn btn-outline">
                            Ver barberos
                        </a>
                    </div>
                </motion.div>
            </section>

            {/* Schedule Section */}
            <section className="schedule-section">
                <div className="schedule-container">
                    <motion.div
                        className="schedule-card"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="schedule-header">
                            <div className="schedule-icon-wrapper">
                                <Clock size={32} />
                            </div>
                            <div className="schedule-title-area">
                                <h2>Horarios de Atención</h2>
                                <p>Agenda tu cita dentro de nuestra jornada laboral</p>
                            </div>
                        </div>

                        <div className="schedule-content">
                            {scheduleBlocks.map((block, idx) => (
                                <div
                                    key={block.label}
                                    className={`schedule-grid-item ${!block.enabled ? 'highlight' : ''}`}
                                >
                                    <div className="day-name">{block.label}</div>
                                    <div className="day-hours">
                                        {block.enabled
                                            ? `${formatTime12h(block.open)} - ${formatTime12h(block.close)}`
                                            : 'Cerrado'}
                                    </div>
                                    <div className="day-status">
                                        {block.enabled ? 'Jornada Continua' : 'Descanso Semanal'}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {holidays.length > 0 && (
                            <div className="schedule-holidays">
                                <div className="schedule-holidays-title">Festivos</div>
                                <div className="schedule-holidays-list">
                                    {holidays.map((h) => {
                                        const dateStr = h.holiday_date || '';
                                        const label = h.day_name && h.day_name !== 'Festivo' ? h.day_name : dateStr;
                                        const hoursStr = h.enabled
                                            ? `${formatTime12h(h.open_time)} - ${formatTime12h(h.close_time)}`
                                            : 'Cerrado';
                                        return (
                                            <div key={h.id || dateStr} className="schedule-holidays-item">
                                                <span className="schedule-holidays-date">{label}</span>
                                                <span className="schedule-holidays-hours">{hoursStr}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="schedule-footer">
                            <div className="status-indicator">
                                <span className="status-dot pulse"></span>
                                <span>Estamos listos para recibirte</span>
                            </div>
                            <Link to="/reservas" className="btn btn-primary">
                                Reserva tu espacio
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Dark CTA Section */}
            <section className="cta-dark">
                <motion.div
                    className="cta-content"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="cta-title">¿Listo para tu <span>nuevo look</span>?</h2>
                    <p className="cta-text">
                        Nuestros barberos expertos están listos para crear el estilo que buscas
                    </p>
                    <Link to="/reservas" className="btn btn-primary btn-lg">
                        <Calendar size={20} />
                        Agenda tu cita ahora
                    </Link>
                    <ImageCarousel images={[image1, image2, image3, image4, image5]} />
                    <SocialVideos />
                </motion.div>
            </section>

            {/* Barbers Section */}
            <section id="barberos" className="services barbers-section">
                <div className="section-header">
                    <p className="section-label">Nuestro equipo</p>
                    <h2 className="section-title">Elige tu <span>Barbero</span></h2>
                    <p className="section-subtitle">Reserva directamente con el profesional que prefieras</p>
                </div>

                <div className="landing-barberos-grid">
                    {loadingBarbers ? (
                        <div className="loading-container" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                            <div className="spinner" style={{
                                width: '40px',
                                height: '40px',
                                border: '3px solid rgba(255,255,255,0.1)',
                                borderTopColor: '#ef4444',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 1rem'
                            }}></div>
                            <p>Cargando barberos...</p>
                            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : barbers.length === 0 ? (
                        <div className="landing-barbers-empty" style={{ gridColumn: '1 / -1' }}>
                            <p>No hay barberos disponibles en este momento.</p>
                            <Link to="/reservas" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                                Ir a reservas
                            </Link>
                        </div>
                    ) : (
                        barbers.map((barber, index) => {
                            const barberName = barber.name || barber.nombre || 'Barbero';
                            const specialty = barber.specialty || barber.especialidad || 'Barbero Profesional';
                            const description = barber.bio || barber.descripcion || 'Experto en cortes modernos y estilo personalizado';
                            const experience = barber.experience_years
                                ? `${barber.experience_years} años de experiencia`
                                : barber.experiencia || 'Experiencia profesional';
                            const imageUrl = barber.image_url
                                ? (barber.image_url.startsWith('http') ? barber.image_url : `${API_BASE_URL}${barber.image_url}`)
                                : null;

                            return (
                                <motion.div
                                    key={barber.id}
                                    className="landing-barbero-card"
                                    whileHover={{ y: -10 }}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="landing-barbero-card-header">
                                        {(barber.available !== false && barber.disponible !== false) && (
                                            <span className="landing-barbero-badge">
                                                <span className="landing-barbero-badge-dot"></span>
                                                Disponible
                                            </span>
                                        )}
                                    </div>
                                    <div className="landing-barbero-avatar">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={barberName} />
                                        ) : (
                                            <User size={48} />
                                        )}
                                    </div>
                                    <div className="landing-barbero-body">
                                        <h3>{barberName}</h3>
                                        <span className="landing-barbero-specialty">{specialty}</span>
                                        <p className="landing-barbero-description">{description}</p>
                                        <div className="landing-barbero-meta">
                                            <span className="landing-barbero-exp">
                                                <Award size={15} />
                                                {experience}
                                            </span>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/reservas?barber=${barber.id}`}
                                        state={{
                                            barber: {
                                                id: barber.id,
                                                name: barberName,
                                                nombre: barberName,
                                                specialty,
                                                especialidad: specialty,
                                                bio: description,
                                                descripcion: description,
                                                experience_years: barber.experience_years,
                                                image_url: barber.image_url,
                                                available: barber.available,
                                                disponible: barber.disponible
                                            }
                                        }}
                                        className="landing-barbero-footer"
                                    >
                                        <span className="select-text">Reservar con {barberName.split(' ')[0]}</span>
                                        <ArrowRight size={18} />
                                    </Link>
                                </motion.div>
                            );
                        })
                    )}
                </div>
            </section>

            {/* Instagram Post Section */}
            <InstagramPost postUrl="https://www.instagram.com/p/DR6evYxDeRk/?img_index=1&igsh=MTlrMXRmZXlyaDY1dA==" />

            {/* Contact Section */}
            <section className="contact-section">
                <div className="contact-container">
                    <motion.div
                        className="contact-header"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="section-title">Contáctanos</h2>
                        <p className="section-subtitle">Estamos aquí para ayudarte</p>
                    </motion.div>

                    <div className="contact-content">
                        {/* WhatsApp Main Button */}
                        <motion.a
                            href="https://wa.me/573005158789"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp-cta-card"
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            whileHover={{ scale: 1.02, y: -5 }}
                        >
                            <div className="whatsapp-cta-icon">
                                <FaWhatsapp size={34} />
                                <div className="pulse-ring"></div>
                            </div>
                            <div className="whatsapp-cta-content">
                                <span className="whatsapp-cta-label">Chatea con nosotros</span>
                                <span className="whatsapp-cta-number">300 515 8789</span>
                                <span className="whatsapp-cta-description">
                                    Respuesta inmediata • Disponible ahora
                                </span>
                            </div>
                            <div className="whatsapp-cta-arrow">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </div>
                        </motion.a>

                        {/* Social Media Grid */}
                        <div className="social-grid">
                            <motion.a
                                href="https://www.instagram.com/genuinosbarbershop_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-card instagram-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="social-card-icon">
                                    <FaInstagram size={28} />
                                </div>
                                <div className="social-card-content">
                                    <span className="social-card-name">Instagram</span>
                                    <span className="social-card-handle">@genuinosbarbershop</span>
                                </div>
                            </motion.a>

                            <motion.a
                                href="https://www.tiktok.com/@genuinos_barber?_r=1&_t=ZS-94cGQ3JAa26"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-card tiktok-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="social-card-icon tiktok-icon">
                                    <FaTiktok size={26} />
                                </div>
                                <div className="social-card-content">
                                    <span className="social-card-name">TikTok</span>
                                    <span className="social-card-handle">@genuinosbarbershop</span>
                                </div>
                            </motion.a>
                        </div>
                    </div>
                </div>
            </section>

            <footer>
                <div className="footer-cta-section">
                    <motion.div
                        className="footer-cta"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="footer-cta-text">
                            <h3>¿Listo para tu próximo corte?</h3>
                            <p>Reserva en segundos y asegura tu turno con el barbero de tu preferencia.</p>
                        </div>
                        <Link to="/reservas" className="footer-cta-btn">
                            Agendar cita
                            <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>

                <div className="footer-wrapper">
                    <div className="footer-main">
                        {/* Brand Column */}
                        <div className="footer-column footer-brand-column">
                            <div className="footer-brand">
                                <div className="footer-logo">
                                    <Scissors size={28} />
                                    <span>GENUINO'S</span>
                                </div>
                                <p className="footer-slogan">Donde el corte también es cultura</p>
                            </div>
                            <p className="footer-description">
                                Tu barbería de confianza en el corazón de la ciudad.
                                Estilo, calidad y tradición en cada corte.
                            </p>
                            <div className="footer-social">
                                <a href="https://www.instagram.com/genuinosbarbershop_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="footer-social-link instagram">
                                    <Instagram size={20} />
                                </a>
                                <a href="https://www.tiktok.com/@genuinos_barber?_r=1&_t=ZS-94cGQ3JAa26" target="_blank" rel="noopener noreferrer" className="footer-social-link tiktok">
                                    <FaTiktok size={18} />
                                </a>
                            </div>
                        </div>

                        {/* Navigation Column */}
                        <div className="footer-column">
                            <h3 className="footer-title">Navegación</h3>
                            <ul className="footer-links">
                                <li><a href="#barberos">Barberos</a></li>
                                <li><Link to="/login">Iniciar Sesión</Link></li>
                            </ul>
                        </div>

                        {/* Services Column */}
                        <div className="footer-column">
                            <h3 className="footer-title">Reservas</h3>
                            <ul className="footer-links">
                                <li><Link to="/reservas">Agendar cita</Link></li>
                                <li><a href="#barberos">Nuestros barberos</a></li>
                            </ul>
                        </div>

                        {/* Contact Column */}
                        <div className="footer-column">
                            <h3 className="footer-title">Contacto</h3>
                            <ul className="footer-contact">
                                <li className="footer-contact-item">
                                    <FaWhatsapp size={18} />
                                    <div>
                                        <p className="contact-label">WhatsApp</p>
                                        <a href="https://wa.me/573005158789" target="_blank" rel="noopener noreferrer">300 515 8789</a>
                                    </div>
                                </li>
                                <li className="footer-contact-item">
                                    <Clock size={18} />
                                    <div>
                                        <p className="contact-label">Horario</p>
                                        <p>Lun - Sáb: 9AM - 8PM</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="footer-bottom">
                        <div className="footer-bottom-content">
                            <p className="footer-copyright">
                                &copy; 2025 Genuino's Barbershop. Todos los derechos reservados.
                            </p>
                            <div className="footer-bottom-links">
                                <a href="#">Términos de Servicio</a>
                                <span className="separator">•</span>
                                <a href="#">Política de Privacidad</a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
